import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingApi, campusApi } from '../services/api';
import { PageHead, Badge, Btn, Card, Spinner, Empty } from '../components/UI';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
} from 'date-fns';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user, isManagement, isAdmin, isViewer } = useAuth();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState(user?.campusID || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bookingDetailsById, setBookingDetailsById] = useState({});
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    const myCampusId = user?.campusID || null;

    campusApi
      .getAll()
      .then((r) => {
        if (!alive) return;

        const allCampuses = r.data.data || [];

        if (isAdmin) {
          setCampuses(allCampuses);
          if (!campusId && allCampuses.length > 0) {
            const preferred = allCampuses.find((c) => c.campusID === myCampusId)?.campusID || allCampuses[0].campusID;
            setCampusId(preferred);
          }
          return;
        }

        const matched = allCampuses.find((c) => c.campusID === myCampusId);
        const ownCampus = matched
          ? [matched]
          : myCampusId
            ? [{ campusID: myCampusId, campusName: user?.campusName || 'My Campus' }]
            : [];

        setCampuses(ownCampus);
        if (myCampusId) setCampusId(myCampusId);
      })
      .catch(() => {
        if (!alive) return;

        if (!isAdmin && myCampusId) {
          setCampuses([{ campusID: myCampusId, campusName: user?.campusName || 'My Campus' }]);
          setCampusId(myCampusId);
        } else {
          setCampuses([]);
        }
      });

    return () => {
      alive = false;
    };
  }, [isAdmin, user?.campusID, user?.campusName]);

  useEffect(() => {
    if (!campusId) {
      setBookings([]);
      setLoading(false);
      return;
    }

    loadBookings();
  }, [currentMonth, campusId]);

  useEffect(() => {
    if (!selectedDate) return;

    const events = eventsForDay(selectedDate);
    if (!events.length) return;

    const missingIds = events
      .map((b) => b.bookingID || b.bookingId || b.id)
      .filter((id) => id && !bookingDetailsById[id]);

    if (!missingIds.length) return;

    let alive = true;
    Promise.allSettled(missingIds.map((id) => bookingApi.getById(id)))
      .then((results) => {
        if (!alive) return;

        const next = {};
        results.forEach((res) => {
          if (res.status !== 'fulfilled') return;
          const full = res.value?.data?.data;
          const id = full?.bookingID || full?.bookingId || full?.id;
          if (id) next[id] = full;
        });

        if (Object.keys(next).length) {
          setBookingDetailsById((prev) => ({ ...prev, ...next }));
        }
      });

    return () => {
      alive = false;
    };
  }, [selectedDate, bookings, bookingDetailsById]);

  async function loadBookings() {
    setLoading(true);
    setError('');

    const from = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
    const to = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

    try {
      const res = await bookingApi.getCalendar(campusId, from, to);
      setBookings(res.data.data || []);
    } catch (err) {
      setBookings([]);
      setError(err?.response?.data?.message || 'Unable to load calendar right now.');
    } finally {
      setLoading(false);
    }
  }

  function buildCells() {
    const cells = [];
    let day = startOfWeek(startOfMonth(currentMonth));
    while (cells.length < 42) {
      cells.push(new Date(day));
      day = addDays(day, 1);
    }
    return cells;
  }

  function matchesFilters(event) {
    const q = search.trim().toLowerCase();
    const statusOk = statusFilter === 'All' || normalizeStatus(event.status) === normalizeStatus(statusFilter);
    const searchOk = !q
      || (event.eventName || '').toLowerCase().includes(q)
      || (event.bookingReference || '').toLowerCase().includes(q)
      || (event.eventArea || event.eventAreaName || '').toLowerCase().includes(q)
      || (event.createdBy || '').toLowerCase().includes(q);
    return statusOk && searchOk;
  }

  function eventsForDay(date) {
    const day = format(date, 'yyyy-MM-dd');
    return bookings.filter((b) => (b.eventDate || '').startsWith(day)).filter((b) => matchesFilters(b));
  }

  const cells = buildCells();

  return (
    <div className="page-anim">
      <PageHead
        title="Calendar"
        subtitle="All Booking LE for the selected month"
        actions={
          <div className="calendar-head-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {isAdmin && campuses.length > 1 && (
              <select
                value={campusId || ''}
                onChange={(e) => setCampusId(parseInt(e.target.value, 10))}
                style={{
                  padding: '7px 12px',
                  fontSize: 13,
                  border: '1.5px solid var(--border)',
                  borderRadius: 8,
                  fontFamily: 'var(--font-body)',
                  outline: 'none',
                  background: 'white',
                }}
              >
                {campuses.map((c) => (
                  <option key={c.campusID} value={c.campusID}>
                    {c.campusName}
                  </option>
                ))}
              </select>
            )}
            {!isViewer && (
              <Btn variant="primary" onClick={() => navigate('/new-booking')}>
                + Booking LE
              </Btn>
            )}
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 14, marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[
          ['var(--green-light)', 'var(--green)', 'Approved'],
          ['var(--orange-light)', 'var(--orange)', 'Pending'],
          ['var(--red-light)', 'var(--red)', 'Rejected'],
        ].map(([bg, c, label]) => (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: c }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: bg, border: `1.5px solid ${c}`, display: 'inline-block' }} />
            {label}
          </span>
        ))}
      </div>
      <Card style={{ marginBottom: '1rem', padding: '0.9rem 1rem' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: '7px 12px', fontSize: 13, border: '1.5px solid var(--border)', borderRadius: 8, fontFamily: 'var(--font-body)', outline: 'none', background: 'white' }}
          >
            <option value="All">All Status</option>
            <option value="Approved">Approved</option>
            <option value="Pending">Pending</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search event, reference, area, requester..."
            style={{ padding: '7px 12px', fontSize: 13, flex: 1, minWidth: 200, border: '1.5px solid var(--border)', borderRadius: 8, outline: 'none', fontFamily: 'var(--font-body)' }}
          />
        </div>
      </Card>

      <Card className="calendar-card">
        <div className="calendar-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ fontFamily: 'var(--font-head)', fontSize: 20, fontWeight: 700 }}>
            {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </div>
          <div className="calendar-toolbar-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <NavBtn onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>{'<'}</NavBtn>
            <Btn size="sm" variant="ghost" onClick={() => { setCurrentMonth(new Date()); setSelectedDate(new Date()); }}>
              Today
            </Btn>
            <NavBtn onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>{'>'}</NavBtn>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem', gap: 10 }}>
            <Spinner size={24} />
            <span style={{ color: 'var(--text2)' }}>Loading...</span>
          </div>
        ) : (
          <>
            {error && (
              <div
                style={{
                  marginBottom: '0.8rem',
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #ffd9d9',
                  background: '#fff3f3',
                  color: '#a72222',
                  fontSize: 13,
                }}
              >
                {error}
              </div>
            )}

            <div className="calendar-grid-wrap" style={{ overflowX: 'auto' }}>
              <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
              {DAYS.map((d) => (
                <div className="calendar-weekday" key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', padding: '6px 0' }}>
                  {d}
                </div>
              ))}

              {cells.map((date, idx) => {
                const other = !isSameMonth(date, currentMonth);
                const today = isToday(date);
                const selected = selectedDate && isSameDay(date, selectedDate);
                const dayEvents = eventsForDay(date);
                const shown = dayEvents.slice(0, 2);
                const extra = dayEvents.length - 2;

                return (
                  <div
                    className="calendar-day-cell"
                    key={idx}
                    onClick={() => setSelectedDate(date)}
                    style={{
                      minHeight: 92,
                      border: `1.5px solid ${selected ? 'var(--blue)' : 'var(--border)'}`,
                      borderRadius: 8,
                      padding: 6,
                      cursor: 'pointer',
                      transition: 'all .15s',
                      background: selected ? '#e0f0fb' : today ? 'var(--blue-light)' : other ? 'var(--bg)' : 'var(--white)',
                      opacity: other ? 0.45 : 1,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        marginBottom: 4,
                        lineHeight: 1,
                        ...(today
                          ? {
                              background: 'var(--blue)',
                              color: 'white',
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 11,
                            }
                          : {}),
                      }}
                    >
                      {date.getDate()}
                    </div>

                    {shown.map((event, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '2px 5px',
                          borderRadius: 4,
                          marginBottom: 2,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          background:
                            event.status === 'Approved'
                              ? 'var(--green-light)'
                              : event.status === 'Pending'
                                ? 'var(--orange-light)'
                                : 'var(--red-light)',
                          color:
                            event.status === 'Approved'
                              ? '#1a7a35'
                              : event.status === 'Pending'
                                ? '#B55A00'
                                : '#b01a1a',
                        }}
                      >
                        {event.eventName}
                      </div>
                    ))}

                    {extra > 0 && <div style={{ fontSize: 10, color: 'var(--blue)', fontWeight: 700 }}>+{extra} more</div>}
                  </div>
                );
              })}
              </div>
            </div>
          </>
        )}
      </Card>

      {selectedDate && (
        <Card style={{ marginTop: '1.2rem' }} className="page-anim">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontFamily: 'var(--font-head)', fontSize: 15, fontWeight: 700 }}>
              {format(selectedDate, 'EEEE, d MMMM yyyy')}
            </div>
            <Btn variant="primary" size="sm" onClick={() => navigate('/new-booking')}>
              + Booking LE
            </Btn>
          </div>

          {eventsForDay(selectedDate).length === 0 ? (
            <Empty icon="[ ]" text="No Booking LE on this day" />
          ) : (
            eventsForDay(selectedDate).map((b) => {
              const bookingId = b.bookingID || b.bookingId || b.id;
              const full = bookingDetailsById[bookingId] || b;
              const assetsText = assetSummary(full.assets || full.Assets || []);

              return (
              <div className="calendar-day-event" key={bookingId} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, border: '1.5px solid var(--border)', borderRadius: 8, marginBottom: 8 }}>
                <div
                  style={{
                    width: 4,
                    minHeight: 50,
                    borderRadius: 4,
                    flexShrink: 0,
                    background: b.status === 'Approved' ? 'var(--green)' : b.status === 'Pending' ? 'var(--orange)' : 'var(--red)',
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{b.eventName}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                    {b.eventArea || b.eventAreaName} | {b.startTime}-{b.endTime} | {b.totalSlots} slot(s)
                  </div>
                  {assetsText !== '--' && (
                    <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                      Assets: {assetsText}
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    By {b.createdBy || '-'} {b.expectedAttendees ? `| Attendees ${b.expectedAttendees}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <Badge status={b.status} />
                  {isManagement && b.status === 'Pending' && (
                    <Btn size="sm" variant="primary" onClick={() => navigate('/approvals')}>
                      Review
                    </Btn>
                  )}
                </div>
              </div>
              );
            })
          )}
        </Card>
      )}

      <style>{`
        @media(max-width:900px){
          .calendar-toolbar{
            flex-direction:column;
            align-items:flex-start!important;
            gap:10px;
          }
        }
        @media(max-width:768px){
          .calendar-head-actions{
            width:100%;
            justify-content:flex-start;
          }
          .calendar-toolbar-actions{
            width:100%;
            justify-content:flex-start;
            flex-wrap:wrap;
          }
          .calendar-grid-wrap{
            overflow-x:auto;
            -webkit-overflow-scrolling:touch;
          }
          .calendar-grid{
            min-width:680px;
            gap:2px!important;
            grid-template-columns:repeat(7,minmax(42px,1fr))!important;
          }
          .calendar-day-cell{
            min-height:74px!important;
            padding:4px!important;
          }
          .calendar-weekday{
            font-size:10px!important;
          }
          .calendar-day-event{
            flex-wrap:wrap;
          }
          .calendar-day-event > div:last-child{
            width:100%;
          }
        }
      `}</style>
    </div>
  );
}

function NavBtn({ children, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: 34,
        height: 34,
        borderRadius: 8,
        background: 'var(--bg2)',
        border: '1.5px solid var(--border)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 15,
        transition: 'all .15s',
      }}
    >
      {children}
    </button>
  );
}

function assetSummary(assets) {
  if (!Array.isArray(assets) || assets.length === 0) return '--';
  return assets
    .map((a) => {
      const name = (a.assetName || `Asset #${a.assetID || a.assetId || ''}`).trim();
      const qty = a.quantityRequested ?? a.quantity ?? 0;
      return `${name} x${qty}`;
    })
    .join(', ');
}

function normalizeStatus(status) {
  return String(status || '').trim().toLowerCase();
}
